import {
  DynamoDBClient,
  GetItemCommand,
  PutItemCommand,
  type PutItemCommandInput,
} from "@aws-sdk/client-dynamodb";
import { marshall, unmarshall } from "@aws-sdk/util-dynamodb";
import { fetchAuthSession } from "aws-amplify/auth";
import type { WorkflowUserStateDto } from "@models/data/workflowUserStateDto";
import { loadCloudConfig, type CloudConfig } from "@models/storage/cloudConfig";
import { registerAwsCredentialClientReset } from "@models/storage/awsCredentialClientRegistry";
import type { WorkflowRunState } from "./WorkflowDocument";
import type {
  IWorkflowUserStateService,
  WorkflowUserStateIdentity,
} from "./IWorkflowUserStateService";

const K_ENTITY_TYPE = "workflow_user_state";
const K_SCHEMA_VERSION = 1;

/**
 * DynamoDB-backed workflow resume state. Local writes are serialized so rapid
 * navigation cannot let an older request complete after a newer replacement.
 */
export class AwsWorkflowUserStateModel implements IWorkflowUserStateService {
  private m_client: DynamoDBClient;
  private readonly m_tableName: string;
  private readonly m_config: CloudConfig;
  private readonly m_unregisterCredentialReset: () => void;
  private m_writeTail: Promise<void> = Promise.resolve();
  private m_retryWrite: PutItemCommandInput | null = null;
  private m_sessionGeneration = 0;

  constructor(config: CloudConfig = loadCloudConfig()) {
    this.m_config = config;
    this.m_tableName = config.telemetryTableName;
    this.m_client = this.createClient();
    this.m_unregisterCredentialReset = registerAwsCredentialClientReset(
      () => this.resetAuthenticatedSession(),
    );
  }

  private createClient(): DynamoDBClient {
    return new DynamoDBClient({
      region: this.m_config.region,
      credentials: async () => {
        const session = await fetchAuthSession();
        const credentials = session.credentials;
        if (!credentials?.accessKeyId || !credentials.secretAccessKey) {
          throw new Error("[AwsWorkflowUserStateModel] No AWS credentials available.");
        }
        return {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
          expiration: credentials.expiration,
        };
      },
    });
  }

  private resetAuthenticatedSession(): void {
    this.m_sessionGeneration += 1;
    this.m_retryWrite = null;
    this.m_writeTail = Promise.resolve();
    this.m_client.destroy();
    this.m_client = this.createClient();
  }

  async getAsync(
    identity: WorkflowUserStateIdentity,
    signal?: AbortSignal,
  ): Promise<WorkflowUserStateDto | null> {
    const normalized = normalizeIdentity(identity);
    // A restart in this tab must observe navigation writes already accepted by
    // the service before it goes to DynamoDB.
    await this.flushPending();
    const response = await this.m_client.send(
      new GetItemCommand({
        TableName: this.m_tableName,
        Key: marshall(keyOf(normalized)),
        ConsistentRead: true,
      }),
      { abortSignal: signal },
    );
    if (!response.Item) return null;
    return parseState(unmarshall(response.Item), normalized);
  }

  putAsync(
    identity: WorkflowUserStateIdentity,
    state: WorkflowRunState,
    signal?: AbortSignal,
  ): Promise<void> {
    const normalized = normalizeIdentity(identity);
    const input = putInput(this.m_tableName, normalized, state);
    const generation = this.m_sessionGeneration;
    const write = this.m_writeTail.then(async () => {
      if (generation !== this.m_sessionGeneration) return;
      // This full replacement supersedes an older failed replacement.
      this.m_retryWrite = null;
      try {
        await this.m_client.send(new PutItemCommand(input), { abortSignal: signal });
      } catch (error) {
        if (generation === this.m_sessionGeneration) this.m_retryWrite = input;
        throw error;
      }
    });

    // Keep the queue usable after a failed request while returning the original
    // promise so the façade can surface this specific failure.
    this.m_writeTail = write.catch(() => undefined);
    return write;
  }

  flushPending(): Promise<void> {
    const generation = this.m_sessionGeneration;
    const flush = this.m_writeTail.then(async () => {
      if (generation !== this.m_sessionGeneration) return;
      const retry = this.m_retryWrite;
      if (!retry) return;

      this.m_retryWrite = null;
      try {
        await this.m_client.send(new PutItemCommand(retry));
      } catch (error) {
        if (generation === this.m_sessionGeneration) this.m_retryWrite = retry;
        throw error;
      }
    });
    this.m_writeTail = flush.catch(() => undefined);
    return flush;
  }

  async dispose(): Promise<void> {
    try {
      await this.flushPending();
    } finally {
      this.m_unregisterCredentialReset();
      this.m_client.destroy();
    }
  }
}

function keyOf(identity: WorkflowUserStateIdentity): { readonly pk: string; readonly sk: string } {
  return {
    pk: `${identity.accountId}:userstate`,
    sk: `workflow:${identity.workflowId}:user:${identity.userId}`,
  };
}

function normalizeIdentity(identity: WorkflowUserStateIdentity): WorkflowUserStateIdentity {
  const normalized = {
    accountId: identity.accountId.trim(),
    userId: identity.userId.trim(),
    projectId: identity.projectId.trim(),
    workflowId: identity.workflowId.trim(),
  };
  if (
    normalized.accountId.length === 0
    || normalized.userId.length === 0
    || normalized.projectId.length === 0
    || normalized.workflowId.length === 0
  ) {
    throw new Error(
      "[AwsWorkflowUserStateModel] accountId, userId, projectId, and workflowId are required.",
    );
  }
  return normalized;
}

function putInput(
  tableName: string,
  identity: WorkflowUserStateIdentity,
  state: WorkflowRunState,
): PutItemCommandInput {
  return {
    TableName: tableName,
    Item: marshall(
      {
        ...keyOf(identity),
        entity_type: K_ENTITY_TYPE,
        schema_version: K_SCHEMA_VERSION,
        account_id: identity.accountId,
        user_id: identity.userId,
        project_id: identity.projectId,
        workflow_id: identity.workflowId,
        run: state.run,
        current: state.current,
        current_group: state.currentGroup,
        current_index: state.currentIndex,
        step_count: state.stepCount,
        completed: [...state.completed],
        visited: [...state.visited],
        visit_stack: [...state.visitStack],
        resume_point: state.resumePoint,
        next: state.next,
        modified_timestamp: new Date().toISOString(),
      },
      { removeUndefinedValues: true },
    ),
  };
}

function parseState(
  raw: Record<string, unknown>,
  identity: WorkflowUserStateIdentity,
): WorkflowUserStateDto {
  if (
    raw.entity_type !== K_ENTITY_TYPE
    || (raw.schema_version !== undefined && raw.schema_version !== K_SCHEMA_VERSION)
    || raw.account_id !== identity.accountId
    || raw.user_id !== identity.userId
    || raw.project_id !== identity.projectId
    || raw.workflow_id !== identity.workflowId
  ) {
    throw new Error(
      "[AwsWorkflowUserStateModel] Stored workflow user state has invalid identity or schema.",
    );
  }

  const run = raw.run;
  if (run !== "in progress" && run !== "not running") {
    throw new Error("[AwsWorkflowUserStateModel] Stored workflow user state has invalid run.");
  }

  return {
    entity_type: K_ENTITY_TYPE,
    schema_version: K_SCHEMA_VERSION,
    account_id: identity.accountId,
    user_id: identity.userId,
    project_id: identity.projectId,
    workflow_id: identity.workflowId,
    run,
    current: nullableString(raw.current),
    currentGroup: nullableString(raw.current_group),
    currentIndex: nullableNumber(raw.current_index),
    stepCount: finiteNumber(raw.step_count, 0),
    completed: stringList(raw.completed),
    // Existing schema-v1 rows from the first prototype did not carry visited.
    visited: stringList(raw.visited),
    // Nor the ordered path. An absent list reads as "no route recorded"; the
    // run model seeds it from the step it resumes on rather than inventing one,
    // so an older row loses the ability to count back but never mis-counts.
    visitStack: stringList(raw.visit_stack),
    resumePoint: nullableString(raw.resume_point),
    next: typeof raw.next === "string" ? raw.next : "",
    modified_timestamp:
      typeof raw.modified_timestamp === "string" ? raw.modified_timestamp : "",
  };
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function nullableNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function stringList(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}
